'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

/**
 * How the presets are displayed.
 * - `pills`: a wrap row of toggle pills (best for desktop).
 * - `select`: a multi-select dropdown (best for narrow/mobile viewports).
 * - `auto` (default): `select` on mobile viewports, `pills` otherwise.
 */
export type QuickFiltersLayout = 'auto' | 'pills' | 'select';

/**
 * How selecting a preset behaves.
 * - `quick` (default): a one-shot shortcut. Clicking a preset applies its facet
 *   values but no active/selected state is maintained — presets never appear
 *   "on", there's no clear-all, and (in `select` layout) the dropdown resets
 *   after a choice. Use when the presets are just fast-paths into the detailed
 *   filters below.
 * - `multi`: a toggle. Presets reflect the current filter state (active when all
 *   their values are selected), toggle off to remove those values, and expose a
 *   clear-all for the active set.
 */
export type QuickFiltersSelectionMode = 'quick' | 'multi';

/**
 * Generic filter value: a map of facet key -> selected option ids. This matches
 * the shape used by ObjectBrowser (`Record<string, string[]>`) as well as the
 * demand inspiration filter object, so QuickFilters can drive either.
 */
export type QuickFilterValue = Record<string, string[]>;

/**
 * A single quick-filter pill. Selecting it applies one or more values across
 * one or more facets at once (e.g. "Product Tasting Collaborations" sets
 * tactics = ['product-sampling', 'collaboration'] and channels = ['retail-in-store']).
 *
 * Only the facets listed in `filters` are affected; other active facet
 * selections are preserved when toggling.
 */
export interface QuickFilterPreset {
  /** Stable identifier for the preset (used as the pill key). */
  id: string;
  /** Display label shown on the pill. */
  label: string;
  /** Partial facet selections applied together when the pill is active. */
  filters: Record<string, string[]>;
}
interface QuickFiltersProps {
  presets: readonly QuickFilterPreset[];
  value: QuickFilterValue;
  onChange: (value: QuickFilterValue) => void;
  /** Optional leading label shown before the pills / as the dropdown label. */
  label?: string;
  /**
   * Display style. Defaults to `auto`: a dropdown on mobile viewports and pills
   * on larger screens. Force `pills` or `select` to override.
   */
  layout?: QuickFiltersLayout;
  /**
   * Selection behaviour. Defaults to `quick` (one-shot shortcuts, no active
   * state). Use `multi` for toggleable, stateful presets with a clear-all.
   */
  selectionMode?: QuickFiltersSelectionMode;
  className?: string;
}

/**
 * Validate and normalise an untrusted value (e.g. JSON loaded from the metadata
 * API) into a list of QuickFilterPreset. Invalid entries are dropped so a
 * malformed config never breaks the caller. Returns `null` when the input isn't
 * a usable array, letting callers fall back to their defaults.
 *
 * @param allowedFacetKeys Optional whitelist of facet keys to keep. When
 *   provided, any facet not in the list is ignored. When omitted, all facet
 *   keys with string-array values are accepted.
 */
export function parseQuickFilterPresets(input: unknown, allowedFacetKeys?: readonly string[]): QuickFilterPreset[] | null {
  if (!Array.isArray(input)) return null;
  const allowed = allowedFacetKeys ? new Set(allowedFacetKeys) : null;
  const presets: QuickFilterPreset[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const candidate = raw as Record<string, unknown>;
    const id = candidate.id;
    const label = candidate.label;
    const filters = candidate.filters;
    if (typeof id !== 'string' || !id) continue;
    if (typeof label !== 'string' || !label) continue;
    if (!filters || typeof filters !== 'object') continue;
    const normalisedFilters: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(filters as Record<string, unknown>)) {
      if (allowed && !allowed.has(key)) continue;
      if (!Array.isArray(values)) continue;
      const stringValues = values.filter((v): v is string => typeof v === 'string');
      if (stringValues.length > 0) normalisedFilters[key] = stringValues;
    }

    // A preset with no valid facet values is meaningless — skip it.
    if (Object.keys(normalisedFilters).length === 0) continue;
    presets.push({
      id,
      label,
      filters: normalisedFilters
    });
  }
  return presets;
}

/** All facet keys touched by a preset. */
function presetKeys(preset: QuickFilterPreset): string[] {
  return Object.keys(preset.filters);
}

/**
 * A preset is "active" when every value it defines is currently selected in the
 * corresponding facet. This keeps the pill in sync even when the user reaches
 * the same selection via the detailed filters.
 */
function isPresetActive(preset: QuickFilterPreset, value: QuickFilterValue): boolean {
  return presetKeys(preset).every(key => {
    const wanted = preset.filters[key] ?? [];
    const current = value[key] ?? [];
    return wanted.every(v => current.includes(v));
  });
}

/**
 * Reusable row of one-click filter presets. Each pill toggles a combination of
 * facet values on a shared `Record<string, string[]>` filter value, so it slots
 * in alongside any detailed filter UI (ObjectBrowser, InspirationFilters,
 * etc.) without any extra state.
 */
/** Add or remove a preset's facet values on `value` (used by `multi` mode). */
function applyPreset(value: QuickFilterValue, preset: QuickFilterPreset, add: boolean): QuickFilterValue {
  const next: QuickFilterValue = {
    ...value
  };
  for (const key of presetKeys(preset)) {
    const wanted = preset.filters[key] ?? [];
    const current = value[key] ?? [];
    next[key] = add ?
    // Add this preset's values, de-duplicating against existing selections.
    Array.from(new Set([...current, ...wanted])) :
    // Remove this preset's values from the facet.
    current.filter(v => !wanted.includes(v));
  }
  return next;
}

/**
 * Replace the quick-filter selection with exactly the chosen preset (used by
 * `quick` mode). First clears every facet any preset manages, then applies the
 * chosen preset's values — so picking a preset fully overrides previous picks
 * rather than accumulating, even when presets touch different facets. Facets no
 * preset manages (e.g. the detailed filters) are left untouched.
 */
function replaceWithPreset(value: QuickFilterValue, preset: QuickFilterPreset, allPresets: readonly QuickFilterPreset[]): QuickFilterValue {
  const next: QuickFilterValue = {
    ...value
  };

  // Clear all facets owned by any preset.
  for (const p of allPresets) {
    for (const key of presetKeys(p)) {
      next[key] = [];
    }
  }

  // Apply the chosen preset's values.
  for (const key of presetKeys(preset)) {
    next[key] = [...(preset.filters[key] ?? [])];
  }
  return next;
}
export function QuickFilters(t0) {
  const $ = _c(73);
  const {
    presets,
    value,
    onChange,
    label,
    layout: t1,
    selectionMode: t2,
    className
  } = t0;
  const layout = t1 === undefined ? "auto" : t1;
  const selectionMode = t2 === undefined ? "quick" : t2;
  const isMobile = useIsMobile();
  const resolvedLayout = layout === "auto" ? isMobile ? "select" : "pills" : layout;
  const isMulti = selectionMode === "multi";
  let t3;
  bb0: {
    if (!isMulti) {
      let t4;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = new Set();
        $[0] = t4;
      } else {
        t4 = $[0];
      }
      t3 = t4;
      break bb0;
    }
    let set;
    if ($[1] !== presets || $[2] !== value) {
      set = new Set();
      for (const preset of presets) {
        if (isPresetActive(preset, value)) {
          set.add(preset.id);
        }
      }
      $[1] = presets;
      $[2] = value;
      $[3] = set;
    } else {
      set = $[3];
    }
    t3 = set;
  }
  const activeIds = t3;
  const anyActive = activeIds.size > 0;
  let t4;
  if ($[4] !== activeIds || $[5] !== isMulti || $[6] !== onChange || $[7] !== presets || $[8] !== value) {
    t4 = preset_0 => {
      if (isMulti) {
        onChange(applyPreset(value, preset_0, !activeIds.has(preset_0.id)));
      } else {
        onChange(replaceWithPreset(value, preset_0, presets));
      }
    };
    $[4] = activeIds;
    $[5] = isMulti;
    $[6] = onChange;
    $[7] = presets;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const togglePreset = t4;
  let t5;
  if ($[10] !== activeIds || $[11] !== onChange || $[12] !== presets || $[13] !== value) {
    t5 = () => {
      let next = value;
      for (const preset_1 of presets) {
        if (activeIds.has(preset_1.id)) {
          next = applyPreset(next, preset_1, false);
        }
      }
      onChange(next);
    };
    $[10] = activeIds;
    $[11] = onChange;
    $[12] = presets;
    $[13] = value;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const clearAll = t5;
  let t6;
  if ($[15] !== activeIds || $[16] !== onChange || $[17] !== presets || $[18] !== value) {
    t6 = nextIds => {
      const nextSet = new Set(nextIds);
      let next_0 = value;
      for (const preset_2 of presets) {
        const wasActive = activeIds.has(preset_2.id);
        const willBeActive = nextSet.has(preset_2.id);
        if (wasActive !== willBeActive) {
          next_0 = applyPreset(next_0, preset_2, willBeActive);
        }
      }
      onChange(next_0);
    };
    $[15] = activeIds;
    $[16] = onChange;
    $[17] = presets;
    $[18] = value;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleMultiSelectionChange = t6;
  let t7;
  if ($[20] !== presets) {
    t7 = presets.map(_temp);
    $[20] = presets;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const options = t7;
  if (presets.length === 0) {
    return null;
  }
  if (resolvedLayout === "select") {
    if (isMulti) {
      const t8 = label ?? null;
      const t9 = label ?? "Quick filter";
      let t10;
      if ($[22] !== activeIds) {
        t10 = Array.from(activeIds);
        $[22] = activeIds;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      let t11;
      if ($[24] !== anyActive || $[25] !== handleMultiSelectionChange || $[26] !== options || $[27] !== t10 || $[28] !== t8 || $[29] !== t9) {
        t11 = <MultiSelectDropdown label={t8} placeholder={t9} options={options} selectedIds={t10} onSelectionChange={handleMultiSelectionChange} showChips={false} showSummaryInTrigger={true} showBulkActions={anyActive} />;
        $[24] = anyActive;
        $[25] = handleMultiSelectionChange;
        $[26] = options;
        $[27] = t10;
        $[28] = t8;
        $[29] = t9;
        $[30] = t11;
      } else {
        t11 = $[30];
      }
      let t12;
      if ($[31] !== className || $[32] !== t11) {
        t12 = <div className={className}>{t11}</div>;
        $[31] = className;
        $[32] = t11;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      return t12;
    }
    let t8;
    if ($[34] !== onChange || $[35] !== presets || $[36] !== value) {
      t8 = id => {
        const preset_4 = presets.find(p => p.id === id);
        if (preset_4) {
          onChange(replaceWithPreset(value, preset_4, presets));
        }
      };
      $[34] = onChange;
      $[35] = presets;
      $[36] = value;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    const t9 = label ?? "Select...";
    let t10;
    if ($[38] !== t9) {
      t10 = <SelectTrigger className="w-full cursor-pointer sm:w-auto sm:min-w-[220px]"><SelectValue placeholder={t9} /></SelectTrigger>;
      $[38] = t9;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    let t11;
    if ($[40] !== presets) {
      t11 = presets.map(_temp2);
      $[40] = presets;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    let t12;
    if ($[42] !== t11) {
      t12 = <SelectContent align="start">{t11}</SelectContent>;
      $[42] = t11;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    let t13;
    if ($[44] !== t10 || $[45] !== t12 || $[46] !== t8) {
      t13 = <Select value="" onValueChange={t8}>{t10}{t12}</Select>;
      $[44] = t10;
      $[45] = t12;
      $[46] = t8;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    let t14;
    if ($[48] !== className || $[49] !== t13) {
      t14 = <div className={className}>{t13}</div>;
      $[48] = className;
      $[49] = t13;
      $[50] = t14;
    } else {
      t14 = $[50];
    }
    return t14;
  }
  let t8;
  if ($[51] !== className) {
    t8 = cn("flex flex-wrap items-center gap-2", className);
    $[51] = className;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  let t9;
  if ($[53] !== label) {
    t9 = label && <span className="mr-1 shrink-0 text-sm font-medium text-gray-800">{label}</span>;
    $[53] = label;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== activeIds || $[56] !== isMulti || $[57] !== presets || $[58] !== togglePreset) {
    let t11;
    if ($[60] !== activeIds || $[61] !== isMulti || $[62] !== togglePreset) {
      t11 = preset_6 => {
        const isSelected = activeIds.has(preset_6.id);
        return <button key={preset_6.id} type="button" aria-pressed={isMulti ? isSelected : undefined} onClick={() => togglePreset(preset_6)} className={cn("shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors", isSelected ? "bg-primary-100 text-primary-700 ring-1 ring-primary-300" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}>{preset_6.label}</button>;
      };
      $[60] = activeIds;
      $[61] = isMulti;
      $[62] = togglePreset;
      $[63] = t11;
    } else {
      t11 = $[63];
    }
    t10 = presets.map(t11);
    $[55] = activeIds;
    $[56] = isMulti;
    $[57] = presets;
    $[58] = togglePreset;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  let t11;
  if ($[64] !== anyActive || $[65] !== clearAll || $[66] !== isMulti) {
    t11 = isMulti && anyActive && <button type="button" onClick={clearAll} className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 whitespace-nowrap px-2 py-1 text-sm text-gray-400 transition-colors hover:text-gray-600"><X className="size-3" aria-hidden="true" />Clear</button>;
    $[64] = anyActive;
    $[65] = clearAll;
    $[66] = isMulti;
    $[67] = t11;
  } else {
    t11 = $[67];
  }
  let t12;
  if ($[68] !== t10 || $[69] !== t11 || $[70] !== t8 || $[71] !== t9) {
    t12 = <div className={t8}>{t9}{t10}{t11}</div>;
    $[68] = t10;
    $[69] = t11;
    $[70] = t8;
    $[71] = t9;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  return t12;
}
function _temp2(preset_5) {
  return <SelectItem key={preset_5.id} value={preset_5.id} className="cursor-pointer">{preset_5.label}</SelectItem>;
}
function _temp(preset_3) {
  return {
    id: preset_3.id,
    label: preset_3.label
  };
}